import React, { useEffect, useState } from "react";
import {
  Flex,
  Box,
  Text,
  Table,
  Tbody,
  Tr,
  Td,
  Spinner,
  Alert,
  AlertIcon,
  useColorModeValue,
  Badge,
  Heading,
  Icon,
  Button,
  HStack,
  useToast,
  SimpleGrid,
  Tag,
  Divider,
} from "@chakra-ui/react";
import { FaUser, FaInfoCircle, FaMapMarkerAlt, FaDna, FaHospital, FaArrowLeft, FaEdit, FaTrash } from "react-icons/fa";
import axios from "axios";
import { getRole, getToken, canManageData } from "utils/auth";
import config from "config";
import { Link, useHistory, useParams } from "react-router-dom";

const Individual = () => {
  const { individual_id } = useParams();
  const toast = useToast();
  const [individual, setIndividual] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const history = useHistory();

  const cardBg = useColorModeValue("white", "navy.700");
  const cardBorder = useColorModeValue("slate.200", "whiteAlpha.100");
  const textColor = useColorModeValue("slate.800", "white");
  const subtitleColor = useColorModeValue("slate.500", "slate.400");
  const subBg = useColorModeValue("slate.50", "navy.800");

  useEffect(() => {
    const fetchIndividual = async () => {
      try {
        const token = getToken();
        const response = await axios.get(
          `${config.API_URL}/individual/${individual_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        }
        );
        setIndividual(response.data);
      } catch (err) {
        setError("Failed to fetch individual profile data");
      } finally {
        setLoading(false);
      }
    };

    fetchIndividual();
  }, [individual_id]);

  const handleDelete = async () => {
    if (window.confirm("Are you sure you want to delete this individual record?")) {
      try {
        const token = getToken();
        await axios.delete(`${config.API_URL}/individual/${individual_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast({
          title: "Individual record deleted.",
          status: "success",
          duration: 4000,
          isClosable: true,
        });
        history.push("/txd/individuals");
      } catch (err) {
        toast({
          title: "Error deleting record.",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
      }
    }
  };

  if (loading) {
    return (
      <Flex justify="center" align="center" minH="60vh">
        <Spinner size="xl" color="teal.500" thickness="4px" />
      </Flex>
    );
  }

  if (error || !individual) {
    return (
      <Flex justify="center" align="center" minH="60vh">
        <Alert status="error" borderRadius="16px" maxW="500px">
          <AlertIcon />
          {error || "Individual not found."}
        </Alert>
      </Flex>
    );
  }

  return (
    <Flex direction="column" w="100%" gap="24px">
      {/* Header Banner & Navigation */}
      <Box
        p={{ base: "14px", sm: "20px", md: "24px" }}
        bg={cardBg}
        borderRadius="24px"
        border="1px solid"
        borderColor={cardBorder}
        shadow="sm"
      >
        <Flex direction={{ base: "column", md: "row" }} justify="space-between" align={{ base: "start", md: "center" }} gap="16px">
          <Flex wrap="wrap" align="center" gap="12px">
            <Button
              as={Link}
              to="/txd/individuals"
              size="sm"
              variant="outline"
              leftIcon={<FaArrowLeft />}
              borderRadius="10px"
            >
              Back to Individuals
            </Button>
            <Box>
              <Flex wrap="wrap" align="center" gap="8px">
                <Heading size="md" color={textColor} fontWeight="800" wordBreak="break-word">
                  Individual Profile: {individual.individual_code}
                </Heading>
                <Badge
                  colorScheme={individual.status === "Healthy" ? "green" : individual.status === "Patient" ? "red" : "yellow"}
                  borderRadius="full"
                  px="12px"
                  py="2px"
                >
                  {individual.status}
                </Badge>
              </Flex>
              <Text fontSize="xs" color={subtitleColor} mt="2px">
                Family Code: <strong>{individual.family?.family_code || "—"}</strong> • Kinship: {individual.kinship || "—"}
              </Text>
            </Box>
          </Flex>

          {canManageData() && (
            <HStack spacing="10px" wrap="wrap">
              <Button
                colorScheme="teal"
                size="sm"
                leftIcon={<FaEdit />}
                as={Link}
                to={`/txd/individuals/edit/${individual_id}`}
                borderRadius="10px"
              >
                Edit Profile
              </Button>
              <Button
                colorScheme="red"
                variant="outline"
                size="sm"
                leftIcon={<FaTrash />}
                onClick={handleDelete}
                borderRadius="10px"
              >
                Delete
              </Button>
            </HStack>
          )}
        </Flex>
      </Box>

      {/* Grid of Profile Detail Cards */}
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing="24px">
        {/* General Information */}
        <Box p="24px" bg={cardBg} borderRadius="24px" border="1px solid" borderColor={cardBorder} shadow="sm">
          <Flex align="center" gap="10px" mb="18px">
            <Icon as={FaInfoCircle} color="teal.500" boxSize="20px" />
            <Heading size="sm" color={textColor}>General Information</Heading>
          </Flex>
          <Table variant="simple" size="sm">
            <Tbody>
              <Tr>
                <Td color={subtitleColor} fontWeight="600" w="40%">Individual Code</Td>
                <Td fontWeight="700" color={textColor}>{individual.individual_code}</Td>
              </Tr>
              <Tr>
                <Td color={subtitleColor} fontWeight="600">Status</Td>
                <Td><Badge colorScheme={individual.status === "Patient" ? "red" : "teal"}>{individual.status}</Badge></Td>
              </Tr>
              <Tr>
                <Td color={subtitleColor} fontWeight="600">Sex</Td>
                <Td fontWeight="600">{individual.sex === "M" ? "Male" : "Female"}</Td>
              </Tr>

              <Tr>
                <Td color={subtitleColor} fontWeight="600">Kinship</Td>
                <Td>{individual.kinship || "—"}</Td>
              </Tr>
              <Tr>
                <Td color={subtitleColor} fontWeight="600">Geographical Origin</Td>
                <Td color="teal.500" fontWeight="600">
                  <Icon as={FaMapMarkerAlt} mr="4px" />
                  {individual?.geographical_origin?.replaceAll("|", " & ") || "—"}
                </Td>
              </Tr>
            </Tbody>
          </Table>
        </Box>

        {/* Family Information */}
        <Box p="24px" bg={cardBg} borderRadius="24px" border="1px solid" borderColor={cardBorder} shadow="sm">
          <Flex align="center" gap="10px" mb="18px">
            <Icon as={FaUser} color="cyan.500" boxSize="20px" />
            <Heading size="sm" color={textColor}>Familial Genealogy</Heading>
          </Flex>
          <Table variant="simple" size="sm">
            <Tbody>
              <Tr>
                <Td color={subtitleColor} fontWeight="600" w="40%">Family Code</Td>
                <Td><Tag colorScheme="cyan" borderRadius="full">{individual.family?.family_code || "—"}</Tag></Td>
              </Tr>
              <Tr>
                <Td color={subtitleColor} fontWeight="600">Consanguinity</Td>
                <Td fontWeight="600">{individual.consanguinity || "—"}</Td>
              </Tr>
              <Tr>
                <Td color={subtitleColor} fontWeight="600">Family History</Td>
                <Td>{individual.family_history || "No recorded history"}</Td>
              </Tr>
            </Tbody>
          </Table>
        </Box>

        {/* Medical Data */}
        <Box p="24px" bg={cardBg} borderRadius="24px" border="1px solid" borderColor={cardBorder} shadow="sm">
          <Flex align="center" gap="10px" mb="18px">
            <Icon as={FaHospital} color="blue.500" boxSize="20px" />
            <Heading size="sm" color={textColor}>Medical Data</Heading>
          </Flex>
          <Table variant="simple" size="sm">
            <Tbody>
              <Tr>
                <Td color={subtitleColor} fontWeight="600" w="40%">Age at Study</Td>
                <Td fontWeight="600">
                  {individual.medical_data?.age_at_study_years != null && `${individual.medical_data.age_at_study_years} yrs `}
                  {individual.medical_data?.age_at_study_months != null && `${individual.medical_data.age_at_study_months} mos`}
                  {individual.medical_data?.age_at_study_years == null && individual.medical_data?.age_at_study_months == null && "—"}
                </Td>
              </Tr>
              <Tr>
                <Td color={subtitleColor} fontWeight="600">Blood Sampling Date</Td>
                <Td>{individual.medical_data?.day_of_blood_sampling || "--"}/{individual.medical_data?.month_of_blood_sampling || "--"}/{individual.medical_data?.year_of_blood_sampling || "----"}</Td>
              </Tr>
              <Tr>
                <Td color={subtitleColor} fontWeight="600">DNA Extraction Date</Td>
                <Td>{individual.medical_data?.day_of_dna_extraction || "--"}/{individual.medical_data?.month_of_dna_extraction || "--"}/{individual.medical_data?.year_of_dna_extraction || "----"}</Td>
              </Tr>
            </Tbody>
          </Table>
        </Box>

        {/* Genetic Data */}
        <Box p="24px" bg={cardBg} borderRadius="24px" border="1px solid" borderColor={cardBorder} shadow="sm">
          <Flex align="center" gap="10px" mb="18px">
            <Icon as={FaDna} color="purple.500" boxSize="20px" />
            <Heading size="sm" color={textColor}>Genomic & Mutation Data</Heading>
          </Flex>
          <Table variant="simple" size="sm">
            <Tbody>
              <Tr>
                <Td color={subtitleColor} fontWeight="600" w="40%">XP Group</Td>
                <Td>
                  {(() => {
                    const raw = individual.genetic_data?.xp_group;
                    const isUnknown = !raw || raw.toLowerCase().includes("unknown") || raw === "—";
                    return (
                      <Badge colorScheme={isUnknown ? "gray" : "purple"} fontSize="xs">
                        {isUnknown ? "Unknown" : raw.replaceAll('|', ', ')}
                      </Badge>
                    );
                  })()}
                </Td>
              </Tr>
              <Tr>
                <Td color={subtitleColor} fontWeight="600">Gene Sequencing</Td>
                <Td fontWeight="600">{individual.genetic_data?.gene_sequencing || "—"}</Td>
              </Tr>
              <Tr>
                <Td color={subtitleColor} fontWeight="600">Exon</Td>
                <Td>{individual.genetic_data?.exon || "—"}</Td>
              </Tr>
              <Tr>
                <Td color={subtitleColor} fontWeight="600">Mutation</Td>
                <Td fontFamily="monospace" fontSize="xs">
                  <strong>c:</strong> {individual.genetic_data?.mutation_c || "—"} | <strong>p:</strong> {individual.genetic_data?.mutation_p || "—"}
                </Td>
              </Tr>
              <Tr>
                <Td color={subtitleColor} fontWeight="600">Genotype</Td>
                <Td fontWeight="700" color="purple.400">{individual.genetic_data?.genotype || "—"}</Td>
              </Tr>
              <Tr>
                <Td color={subtitleColor} fontWeight="600">Remarks</Td>
                <Td>{individual.genetic_data?.remarks || "None"}</Td>
              </Tr>
            </Tbody>
          </Table>
        </Box>
      </SimpleGrid>
    </Flex>
  );
};

export default Individual;

